import React, { useState, useEffect, useRef } from "react";
import LandingSeo from "../components/LandingSeo";
import Index from "../view/Landing/Index";

import authorImg from "../assest/images/chat4.jpg";
import avatar1 from "../assest/images/chat1.jpg";
import avatar2 from "../assest/images/chat2.jpg";

const HERO_FEATURES = [
    {
        icon: "fa-solid fa-globe",
        title: "Global Exposure",
        text: "Get your tours and activities in front of millions of travelers worldwide.",
    },
    {
        icon: "fa-solid fa-chart-column",
        title: "More Bookings",
        text: "Increase your visibility and receive more bookings, more often.",
    },
    {
        icon: "fa-solid fa-shield-halved",
        title: "Trusted Platform",
        text: "A secure and reliable platform designed for travel professionals.",
    },
];

const VISIBILITY_ITEMS = [
    {
        icon: "fa-solid fa-arrow-trend-up",
        title: "Top Search Rankings",
        text: "Get your products to the top of search results and category pages.",
    },
    {
        icon: "fa-solid fa-bolt",
        title: "Higher Conversions",
        text: "More visibility leads to more clicks, bookings and revenue.",
    },
    {
        icon: "fa-solid fa-location-dot",
        title: "Targeted Exposure",
        text: "Reach the right audience looking for experiences like yours.",
    },
    {
        icon: "fa-solid fa-bullseye",
        title: "Visibility Credits",
        text: "Use visibility credits to boost your rankings and stay ahead.",
    },
];

const STEPS = [
    {
        icon: "fa-solid fa-user-plus",
        title: "1. Create Account",
        text: "Sign up and complete your supplier profile.",
    },
    {
        icon: "fa-solid fa-calendar-plus",
        title: "2. Add Your Products",
        text: "List your tours, activities and availability.",
    },
    {
        icon: "fa-solid fa-award",
        title: "3. Get More Bookings",
        text: "Reach millions of travelers and grow your business.",
    },
];

const BENEFITS = [
    {
        icon: "fa-solid fa-list-check",
        title: "Easy Management",
        text: "Manage your products, availability and bookings with ease.",
        color: "#16a34a",
        bg: "#e6f6ec",
    },
    {
        icon: "fa-solid fa-shield-halved",
        title: "Secure Payments",
        text: "Get paid securely and on time with our trusted system.",
        color: "#7c3aed",
        bg: "#f1e9fd",
    },
    {
        icon: "fa-solid fa-calendar-days",
        title: "Real-time Availability",
        text: "Sync your availability in real-time and avoid double bookings.",
        color: "#2563eb",
        bg: "#e6effd",
    },
    {
        icon: "fa-solid fa-chart-column",
        title: "Performance Insights",
        text: "Track your performance and understand your business better.",
        color: "#e08a00",
        bg: "#fdf1d8",
    },
    {
        icon: "fa-solid fa-headset",
        title: "24/7 Support",
        text: "Our support team is always here to help you succeed.",
        color: "#4f46e5",
        bg: "#e9e8fc",
    },
    {
        icon: "fa-solid fa-circle-check",
        title: "No Setup Fees",
        text: "Join for free and start growing your business today.",
        color: "#0d9488",
        bg: "#d7f1ee",
    },
];

const STATS = [
    { icon: "fa-solid fa-users", end: 5000, compact: false, label: "Active Suppliers" },
    { icon: "fa-solid fa-suitcase-rolling", end: 25000, compact: false, label: "Tours & Activities" },
    { icon: "fa-solid fa-face-smile", end: 1000000, compact: true, label: "Happy Travelers" },
    { icon: "fa-solid fa-globe", end: 150, compact: false, label: "Countries Covered" },
];

// Dummy testimonials — replace/extend with real records later
const TESTIMONIALS = [
    {
        quote: "ToursXplorer has been a game changer for our business. We reached more customers and increased our bookings by 40% in just a few months!",
        name: "Sarah M.",
        role: "Adventure Seeker Tours",
        avatar: authorImg,
    },
    {
        quote: "Setup was effortless and the real-time availability sync saved us from countless double bookings. Support is genuinely 24/7.",
        name: "David R.",
        role: "Coastal Kayak Co.",
        avatar: avatar1,
    },
    {
        quote: "The visibility tools put our tours in front of the right travelers. Our revenue grew steadily month over month.",
        name: "Elena K.",
        role: "Old Town Walking Tours",
        avatar: avatar2,
    },
];

export default function LandingController() {
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const nextTestimonial = () => setActiveTestimonial((i) => (i + 1) % TESTIMONIALS.length);
    const prevTestimonial = () => setActiveTestimonial((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
    const testimonial = TESTIMONIALS[activeTestimonial];

    // Auto-advance the testimonials every 4.5s
    useEffect(() => {
        const id = setInterval(() => {
            setActiveTestimonial((i) => (i + 1) % TESTIMONIALS.length);
        }, 4500);
        return () => clearInterval(id);
    }, []);

    // Reveal the visibility section as it scrolls into view (draws focus)
    const visRef = useRef(null);
    useEffect(() => {
        const el = visRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        el.classList.add("lp-revealed");
                        observer.unobserve(el);
                    }
                });
            },
            { threshold: 0.2 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <LandingSeo />
            <Index
                heroFeatures={HERO_FEATURES}
                visibilityItems={VISIBILITY_ITEMS}
                steps={STEPS}
                benefits={BENEFITS}
                stats={STATS}
                testimonials={TESTIMONIALS}
                activeTestimonial={activeTestimonial}
                setActiveTestimonial={setActiveTestimonial}
                nextTestimonial={nextTestimonial}
                prevTestimonial={prevTestimonial}
                testimonial={testimonial}
                visRef={visRef}
            />
        </>
    );
}
